import * as Sentry from '@sentry/react'
import { type ZodError } from 'zod'

import { initSentry } from './initSentry'

export type LogLevel = Sentry.SeverityLevel

export type LogOptions = {
  /**
   * Feature area (e.g., 'auth', 'billing', 'search')
   */
  category?: string
  /**
   * Queryable filter metrics
   */
  tags?: Record<string, string | number | boolean>
  /**
   * Rich debugging metadata payloads
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: Record<string, any>
}

export type UserProfile = {
  id: string
  /**
   * Any other optional custom fields
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export class Logger {
  static init(dsn: string, appName: string) {
    initSentry({ dsn, appName })
  }

  private static logToProvider(level: LogLevel, options: LogOptions, action: () => void) {
    Sentry.withScope((scope) => {
      scope.setLevel(level)
      if (options.category) scope.setTag('log_category', options.category)
      if (options.tags) scope.setTags(options.tags)
      if (options.context) scope.setExtras(options.context)

      action()
    })
  }

  /**
   * Logs an explicit code failure or unexpected runtime exception block.
   */
  static error(error: unknown, options: LogOptions = {}) {
    Logger.logToProvider('error', options, () => {
      Sentry.captureException(error)
    })
  }

  /**
   * Captures an operational system status notice or warning message signature
   */
  static warn(message: string, options: LogOptions = {}) {
    Logger.logToProvider('warning', options, () => {
      Sentry.captureMessage(message)
    })
  }

  /**
   * Dispatches a standard informational log notification
   */
  static info(message: string, options: LogOptions = {}) {
    Logger.logToProvider('info', options, () => {
      Sentry.captureMessage(message)
    })
  }

  /**
   * Binds user context to tracking logs for downstream isolation searches
   */
  static setUser(user: UserProfile | null) {
    if (!user) {
      Sentry.setUser(null)
      return
    }

    Sentry.setUser({ ...user, id: user.id })
  }

  static zodError(error: ZodError, options: LogOptions = {}) {
    if (import.meta.env.DEV) {
      // In development, log the error details to the console for easier debugging
      console.error('Zod Validation Error:', error)
    }

    const combinedOptions: LogOptions = {
      ...options,
      category: 'zod-parsing-error',
      context: {
        ...options.context,
        issues: error.issues.map(({ path, code, message }) => ({
          path: path.join('.'),
          code,
          message,
        })),
      },
    }

    Logger.logToProvider('error', combinedOptions, () => {
      Sentry.captureException(error)
    })
  }
}
